<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <b-dropdown id="unduh-dropdown" right variant="primary" menu-class="dropdown-menu-end">
              <template v-slot:button-content>
                <i class="mc-fa icon-outline_download"></i>{{ $t('global.action.download') }}
              </template>
              <b-dropdown-item href="#" @click="downloadReport('xlsx')">
                <img :src="excelIcon" alt="excel" />
                {{ $t('global.action.downloadExcel') }}
              </b-dropdown-item>
              <b-dropdown-item href="#" @click="downloadReport('pdf')">
                <img :src="pdfIcon" alt="pdf" />
                {{ $t('global.action.downloadPdf') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <div style="margin-top: 120px">
      <div v-for="(timeWindowItem, index) in reportTimeWindow" :key="index">
        <b-row class="text-muted mc-report-view__label-info">
          <b-col sm="2">{{ $t('report.title.reportType') }}</b-col>
          <b-col sm="2">{{ $t('report.object.reportPeriod') }}</b-col>
          <b-col sm="1">{{ $t('global.object.fleet') }}</b-col>
          <b-col sm="1">{{ $t('report.fields.garage') }}</b-col>
          <b-col sm="2">{{ $t('report.fields.outGarage') }}</b-col>
          <b-col sm="2">{{ $t('report.fields.inGarage') }}</b-col>
          <b-col sm="2">{{ $t('report.fields.cycleDuration') }}</b-col>
        </b-row>
        <b-row class="mc-report-view__text-info">
          <b-col sm="2">
            <span>{{ $t('report.report.timeWindow') }}</span>
          </b-col>
          <b-col>
            <span>{{ periodeLabel }}</span>
          </b-col>
          <b-col sm="1"
            ><span>{{ licensePlateLabel }}</span></b-col
          >
          <b-col sm="1"
            ><span>{{ geofenceLabel }}</span></b-col
          >
          <b-col sm="2"
            ><span v-if="timeWindowItem.out"
              >{{ momentDateShortMonth(timeWindowItem.out) }}
              {{ momentTimeSeconds(timeWindowItem.out) }}</span
            ><span v-else>-</span></b-col
          >
          <b-col sm="2"
            ><span v-if="timeWindowItem.in"
              >{{ momentDateShortMonth(timeWindowItem.in) }}
              {{ momentTimeSeconds(timeWindowItem.in) }}</span
            ><span v-else>-</span></b-col
          >
          <b-col sm="2"
            ><span>{{ timeWindowItem.time ? timeWindowItem.time : '-' }}</span></b-col
          >
        </b-row>
        <b-card :key="summaryKey" class="mt-3">
          <div class="mc-report-view__table">
            <div class="row">
              <!-- Search -->
              <div class="col-md-12">
                <div class="mc-table-view">
                  <div id="tickets-table_filter" class="dataTables_filter text-md-end float-end">
                    <label
                      class="d-inline-flex align-items-center mc-table-view__search"
                      @click="focusSearch"
                    >
                      <i class="mc-fa fas fa-search"></i>
                      <b-form-input
                        v-model="filter[index]"
                        type="search"
                        :placeholder="$t('global.search') + '...'"
                        class="ms-2"
                        ref="searchInput"
                      />
                    </label>
                  </div>
                </div>
              </div>
              <!-- End search -->
            </div>

            <div class="table-responsive my-3 border border-1">
              <b-skeleton-wrapper :loading="isLoading">
                <template #loading>
                  <b-card>
                    <b-skeleton-table
                      :rows="3"
                      :columns="fields.length"
                      :table-props="{ bordered: false, striped: true }"
                    />
                  </b-card>
                </template>
                <b-table
                  class="datatables"
                  :items="timeWindowItem.data"
                  :fields="fields"
                  responsive="xl"
                  show-empty
                  :empty-text="$t('global.emptyTable')"
                  :per-page="perPage[index]"
                  :current-page="currentPage[index]"
                  :empty-filtered-text="$t('global.emptyFilteredTable')"
                  :filter="filter[index]"
                  :filter-included-fields="filterOn"
                  @filtered="onFiltered($event, index)"
                  filter-debounce="500"
                >
                  <template #cell(fenceName)="data">
                    <template v-if="data.item.geofence.fenceName">
                      {{ data.item.geofence.fenceName ? data.item.geofence.fenceName : '-' }}<br />
                    </template>
                    <span v-else>-</span>
                  </template>

                  <template #cell(transitOn)="data">
                    <template v-if="data.item.transitOn">
                      {{ data.item.transitOn ? momentDateShortMonth(data.item.transitOn) : '-'
                      }}<br />
                      <span class="text-muted">{{
                        data.item.transitOn ? momentTimeSeconds(data.item.transitOn) : '-'
                      }}</span>
                    </template>
                    <span v-else>-</span>
                  </template>

                  <template #cell(doorOpen)="data">
                    <template v-if="data.item.doorOpen">
                      {{ data.item.doorOpen ? momentDateShortMonth(data.item.doorOpen) : '-'
                      }}<br />
                      <span class="text-muted">{{
                        data.item.doorOpen ? momentTimeSeconds(data.item.doorOpen) : '-'
                      }}</span>
                    </template>
                    <span v-else>-</span>
                  </template>

                  <template #cell(doorClose)="data">
                    <template v-if="data.item.doorClose">
                      {{ data.item.doorClose ? momentDateShortMonth(data.item.doorClose) : '-'
                      }}<br />
                      <span class="text-muted">{{
                        data.item.doorClose ? momentTimeSeconds(data.item.doorClose) : '-'
                      }}</span>
                    </template>
                    <span v-else>-</span>
                  </template>

                  <template #cell(transitOnOut)="data">
                    <template v-if="data.item.transitOnOut">
                      {{
                        data.item.transitOnOut ? momentDateShortMonth(data.item.transitOnOut) : '-'
                      }}<br />
                      <span class="text-muted">{{
                        data.item.transitOnOut ? momentTimeSeconds(data.item.transitOnOut) : '-'
                      }}</span>
                    </template>
                    <span v-else>-</span>
                  </template>

                  <template #cell(timeInQueue)="data">
                    <template>
                      {{ data.item.timeInQueue ? data.item.timeInQueue : '-' }}
                    </template>
                  </template>

                  <template #cell(timeLoading)="data">
                    <template>
                      {{ data.item.timeLoading ? data.item.timeLoading : '-' }}
                    </template>
                  </template>

                  <template #cell(timeOutQueue)="data">
                    <template>
                      {{ data.item.timeOutQueue ? data.item.timeOutQueue : '-' }}
                    </template>
                  </template>

                  <template #cell(timeInGeofence)="data">
                    <template>
                      {{ data.item.timeInGeofence ? data.item.timeInGeofence : '-' }}
                    </template>
                  </template>

                  <template #cell(timeBetweenGeofence)="data">
                    <template>
                      {{ data.item.timeBetweenGeofence ? data.item.timeBetweenGeofence : '-' }}
                    </template>
                  </template>

                  <template slot="bottom-row" v-if="filteredItems[index].length > 0">
                    <b-td colspan="5">
                      <template> Rata-Rata </template>
                    </b-td>
                    <b-td v-for="field in footerArr" :key="field.key">
                      <template>
                        {{ convertToTime(totalFooter[index][field] / filteredItems[index].length) }}
                      </template>
                    </b-td>
                  </template>
                </b-table>
              </b-skeleton-wrapper>
            </div>
            <div class="row mt-3" v-if="loaded">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    {{ $t('global.text.displays') }}
                    <b-form-select
                      class="form-select form-select-sm"
                      v-model="perPage[index]"
                      size="sm"
                      :options="pageOptions"
                    />
                    {{ $t('global.text.entries') }}.
                    <p>
                      {{ $t('global.text.display') }}
                      <strong>{{
                        filteredItems[index].length > 0
                          ? (currentPage[index] - 1) * perPage[index] + 1
                          : 0
                      }}</strong>
                      -
                      <strong>{{
                        Math.min(filteredItems[index].length, currentPage[index] * perPage[index])
                      }}</strong>
                      {{ $t('global.object.of') }}
                      <strong>{{ filteredItems[index].length }}</strong>
                    </p>
                  </label>
                </div>
              </div>
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <ul class="pagination pagination-rounded mb-0">
                    <!-- pagination -->
                    <b-pagination
                      v-model="currentPage[index]"
                      :total-rows="
                        filteredItems[index] == null
                          ? filteredItems[index].length
                          : filteredItems[index].length
                      "
                      :per-page="perPage[index]"
                    />
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </div>
      <div v-if="currentPage.length === 0 && fields.length > 0">
        <b-row class="text-muted mc-report-view__label-info">
          <b-col sm="2">{{ $t('report.title.reportType') }}</b-col>
          <b-col sm="2">{{ $t('report.object.reportPeriod') }}</b-col>
          <b-col sm="1">{{ $t('global.object.fleet') }}</b-col>
          <b-col sm="1">{{ $t('report.fields.garage') }}</b-col>
          <b-col sm="2">{{ $t('report.fields.outGarage') }}</b-col>
          <b-col sm="2">{{ $t('report.fields.inGarage') }}</b-col>
          <b-col sm="2">{{ $t('report.fields.cycleDuration') }}</b-col>
        </b-row>
        <b-row class="mc-report-view__text-info">
          <b-col md="2">
            <span>{{ $t('report.report.timeWindow') }}</span>
          </b-col>
          <b-col md="2">
            <span>{{ periodeLabel }}</span>
          </b-col>
          <b-col sm="1"
            ><span>{{ licensePlateLabel }}</span></b-col
          >
          <b-col sm="1"
            ><span>{{ geofenceLabel }}</span></b-col
          >
          <b-col sm="2"><span>-</span></b-col>
          <b-col sm="2"><span>-</span></b-col>
          <b-col sm="2"><span>-</span></b-col>
        </b-row>

        <b-card class="mt-3">
          <div class="mc-report-view__table">
            <div class="row">
              <!-- Search -->
              <div class="col-md-12">
                <div class="mc-table-view">
                  <div id="tickets-table_filter" class="dataTables_filter text-md-end float-end">
                    <label
                      class="d-inline-flex align-items-center mc-table-view__search"
                      @click="focusSearch"
                    >
                      <i class="mc-fa fas fa-search"></i>
                      <b-form-input
                        type="search"
                        :placeholder="$t('global.search') + '...'"
                        class="ms-2"
                        ref="searchInput"
                      />
                    </label>
                  </div>
                </div>
              </div>
              <!-- End search -->
            </div>

            <div class="table-responsive my-3 border border-1">
              <b-skeleton-wrapper :loading="isLoading">
                <template #loading>
                  <b-card>
                    <b-skeleton-table
                      :rows="3"
                      :columns="fields.length"
                      :table-props="{ bordered: false, striped: true }"
                    />
                  </b-card>
                </template>
                <b-table
                  class="datatables"
                  responsive="xl"
                  show-empty
                  :items="reportTimeWindow"
                  :fields="fields"
                  :empty-text="$t('global.emptyTable')"
                  :per-page="todoperPage"
                  :empty-filtered-text="$t('global.emptyFilteredTable')"
                  :filter-included-fields="filterOn"
                />
              </b-skeleton-wrapper>
            </div>
            <div class="row mt-3">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    {{ $t('global.text.displays') }}
                    <b-form-select
                      class="form-select form-select-sm"
                      v-model="todoperPage"
                      size="sm"
                      :options="pageOptions"
                    />
                    {{ $t('global.text.entries') }}.
                    <p>
                      {{ $t('global.text.display') }} <strong>0</strong> - <strong>0 </strong>
                      {{ $t('global.object.of') }}
                      <strong>0</strong>
                    </p>
                  </label>
                </div>
              </div>
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <ul class="pagination pagination-rounded mb-0">
                    <!-- pagination -->
                    <b-pagination :total-rows="1 == null ? 1 : 1" :per-page="todoperPage" />
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </div>
      <div class="row mt-3" v-if="fields.length > 0 && rows > 0">
        <div class="col-sm-12 col-md-6">
          <div id="tickets-table_length" class="dataTables_length">
            <label class="d-inline-flex align-items-center">
              {{ $t('global.text.displays') }}
              <b-form-select
                class="form-select form-select-sm"
                v-model="listPerPage"
                size="sm"
                :options="pageOptions"
                @input="changePerPage"
              />
              {{ $t('global.text.entries') }}.
              <p>
                {{ $t('global.text.display') }}&nbsp;<strong>{{
                  totalRowsList > 0 ? (listCurrentPage - 1) * listPerPage + 1 : 0
                }}</strong>
                - <strong>{{ Math.min(totalRowsList, listCurrentPage * listPerPage) }}</strong>
                {{ $t('global.object.of') }}
                <strong>{{ totalRowsList }}</strong
                >&nbsp;{{ $t('global.entry') }}.
              </p>
            </label>
          </div>
        </div>
        <div class="col-sm-12 col-md-6">
          <div class="dataTables_paginate paging_simple_numbers float-end">
            <ul class="pagination pagination-rounded mb-0">
              <!-- pagination -->
              <b-pagination
                v-model="listCurrentPage"
                :total-rows="totalRowsList"
                :per-page="listPerPage"
                @change="changePage"
              />
            </ul>
          </div>
        </div>
      </div>
    </div>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
    <ModalSavedReportVerification
      :key="modalVerifReport"
      :domain="domain"
      :message="message"
      :error="isError"
    />
  </Layout>
</template>
<script>
import McButton from '@/components/McButton'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McReportDownloader from '@/components/McReportDownloader'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import fields from './lib/fields/fields'
import { TIME_WINDOW_REPORT, GET_REPORT_TIME_WINDOW } from '@/services/api/report/report'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import reportList from '../../lib/reportList.js'
import { MixPanel } from '@/mixin/mixpanel'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import { mapGetters } from 'vuex'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import responsive from '@/mixin/responsive'
moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McReportDownloader,
    McButton,
    ModalFilter,
    ModalSavedReport,
    ModalSavedReportVerification,
    FavoriteReportButton,
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      filters: {},
      tableKey: 0,
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      tableData: [],
      todoTotalRows: 1,
      currentPage: [],
      todocurrentPage: 1,
      perPage: [],
      todoperPage: 10,
      pageOptions: [10, 25, 50, 100],
      todoOptions: [10, 50, 100, 150, 200],
      filter: [],
      filterOn: [],
      reportTimeWindow: [],
      isLoading: false,
      fileName: '',
      openSection: false,
      periode: [],
      filteredItems: [],
      totalFooter: [],
      timer: null,
      loaded: true,
      footerArr: [
        'timeInQueue',
        'timeLoading',
        'timeOutQueue',
        'timeInGeofence',
        'timeBetweenGeofence',
      ],
      listCurrentPage: 1,
      totalRowsList: 0,
      listPerPage: 10,
      reportKey: 'time-window',
      filterDetail: {},
      summaryKey: 50,
    }
  },
  mounted() {
    this.reportKey =
      this.$route.query.key && this.savedReport.find((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.totalRowsList = this.reportTimeWindow.pagination?.total ?? 0
    setTimeout(() => {
      if (Object.keys(this.filters).length > 0) this.refreshData()
    }, 500)
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    rows() {
      return this.reportTimeWindow.length
    },
    listData() {
      return this.reportTimeWindow
    },
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    fields() {
      return this.reportFields
    },
    columns() {
      return this.reportFields
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const filters = {}
        Object.keys(this.filters).forEach((item) => {
          if (
            this.filters[item] !== null &&
            ((Array.isArray(this.filters[item]) && this.filters[item].length > 0) ||
              (!Array.isArray(this.filters[item]) && this.filters[item]))
          ) {
            filters[item] = this.filters[item]
          }
        })
        const { periode, garageId, ...otherFilter } = filters
        const startDate =
          periode.length > 0 ? moment(periode[0] + ' 00:00:00.000').toISOString() : null
        const endDate =
          periode.length > 0 ? moment(periode[1] + ' 23:59:59.000').toISOString() : null
        const model = {
          startDate,
          endDate,
          ...otherFilter,
          geofenceId: garageId ?? undefined,
        }
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDateShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDateShortMonth(this.filters.periode[1])
        : '-'
    },
    licensePlateLabel() {
      if (this.filters.vehicleId) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else return '-'
    },
    geofenceLabel() {
      if (this.filters.garageId) {
        return this.filterDetail != {} ? this.filterDetail.geofence : '-'
      } else return '-'
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    handleColumnReport(fields) {
      this.reportFields = fields
      this.tableKey += 1
      this.summaryKey += 1
    },
    forceRerender() {
      this.filters = { ...this.$store.state.report.filterReport }
      this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      this.refreshData()
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    changePerPage(perPage) {
      this.listCurrentPage = 1
      this.listPerPage = perPage
      this.refreshData()
    },
    changePage(page) {
      this.listCurrentPage = page
      this.refreshData()
    },
    initResult(data) {
      var res = []
      var self = this
      if (data.length === 0) {
        this.currentPage = []
        this.perPage = []
      }
      data.forEach(function (item, index) {
        self.currentPage[index] = 1
        self.perPage[index] = 10
        self.filteredItems[index] = item.data

        if (self.totalFooter[index] === undefined) {
          self.totalFooter[index] = {
            timeInQueue: 0,
            timeLoading: 0,
            timeOutQueue: 0,
            timeInGeofence: 0,
            timeBetweenGeofence: 0,
          }
        }

        item.data.forEach(function (res) {
          self.totalFooter[index].timeInQueue += self.convertToSeconds(res.timeInQueue)
          self.totalFooter[index].timeLoading += self.convertToSeconds(res.timeLoading)
          self.totalFooter[index].timeOutQueue += self.convertToSeconds(res.timeOutQueue)
          self.totalFooter[index].timeInGeofence += self.convertToSeconds(res.timeInGeofence)
          self.totalFooter[index].timeBetweenGeofence += self.convertToSeconds(
            res.timeBetweenGeofence
          )
        })
      })
      return res
    },
    onFiltered(item, index) {
      this.loaded = !this.loaded
      this.filteredItems[index] = item
      this.currentPage[index] = 1
      this.perPage[index] = 10

      this.$nextTick(() => {
        this.loaded = !this.loaded
      })
    },
    refreshData() {
      const { valid, model } = this.generateFilter
      if (!valid) return {}
      var self = this
      self.isLoading = true
      var getData = this.$apollo
        .query({
          query: GET_REPORT_TIME_WINDOW,
          variables: {
            filter: {
              ...model,
              column: this.columns,
              pagination: {
                page: this.listCurrentPage,
                show: this.listPerPage,
              },
            },
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          var data = res.data
          self.totalRowsList = data.reportTimeWindow.reportTimeWindow.pagination.total
          if (data.reportTimeWindow.reportTimeWindow.data.length === 0) {
            self.reportTimeWindow = []
            self.isLoading = false
            return []
          }
          self.isEmpty = false
          var results = self.initResult(data.reportTimeWindow.reportTimeWindow.data)
          self.reportTimeWindow = data.reportTimeWindow.reportTimeWindow.data
          self.isLoading = false
          self.message = data.reportTimeWindow.message
          self.isError = !data.reportTimeWindow.isSuccessful
          self.modalVerifReport += 1
          return results
        })

      return getData
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name:
          `${this.title} ` +
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY') +
          `.${extension}`,
        periode:
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id: result.timeWindowReport.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension) {
      this.track(`Download ${this.report.event} ${extension == 'pdf' ? '- PDF' : '- Excel'}`)

      const { valid, model } = this.generateFilter
      if (!valid) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }
      const { periode } = this.filters
      let filterVar = {
        ...model,
        column: this.columns,
        downloadAs: extension == 'pdf' ? 'pdf' : 'excel',
      }

      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }

      this.$apollo
        .query({
          query: TIME_WINDOW_REPORT,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          this.showDownloader(periode, extension, res.data.timeWindowReport)
        })
    },
  },
}
</script>
